import React, { useEffect } from 'react';
import TimezoneSelect, { allTimezones } from '@mahhoura/react-timezone-select-mui';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { Box, Button, type SelectProps } from '@mui/material';
import { useSnackbar } from 'notistack';

// The package types its props as react-select's, but it renders MUI's Select: it
// reads `label`, `helperText` and `variant` itself and passes the remaining props
// (such as `disabled`) to the Select.
const MuiTimezoneSelect = TimezoneSelect as (
  props: React.ComponentProps<typeof TimezoneSelect> &
    Pick<SelectProps, 'disabled' | 'variant'> & {
      label?: React.ReactNode;
      helperText?: React.ReactNode;
    },
) => ReturnType<typeof TimezoneSelect>;

export default function TimeZoneSelector() {
  const snackbar = useSnackbar();

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  const {
    isLoading,
    userProfile,
    actions: { updateTimezone },
  } = useUserStateContext();
  const [selectedTimezone, setSelectedTimezone] = React.useState(userProfile?.tz || timeZone);

  const handleChange = (changedTZ: any) => {
    setSelectedTimezone(changedTZ.value);
  };

  useEffect(() => {
    if (userProfile?.tz) {
      setSelectedTimezone(userProfile.tz);
    } else {
      setSelectedTimezone(timeZone);
    }
  }, [userProfile, timeZone]);

  if (userProfile?.tz === undefined) {
    return <></>;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await updateTimezone(selectedTimezone as string);
    snackbar.enqueueSnackbar('User timezone updated', { variant: 'success' });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, mt: 4 }}>
      <MuiTimezoneSelect
        disabled={isLoading}
        onChange={handleChange}
        timezones={allTimezones}
        label="Timezone"
        helperText={`Your browser's timezone is ${timeZone}`}
        value={selectedTimezone}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{ mt: 2, mx: 'auto', display: 'block' }}
      >
        Update
      </Button>
    </Box>
  );
}
